import {
	changeDeal,
	getDealData,
	getDealsListData,
} from '@dealstack/frontend/shared/dataExchange/services/god.service';
import { logger } from '@dealstack/frontend/shared/mechanism/logger/logger';
import { useDSToast } from '@dealstack/frontend/shared/mechanism/toast/useDSToast';
import { SummaryDealTct } from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	ColumnDef,
	getCoreRowModel,
	getSortedRowModel,
	SortingState,
	useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

interface ISelectedDeal {
	id: string;
	name: string;
}
const EMPTY_DATA: SummaryDealTct[] = [];
export const useDeal = (refetchMipData: () => void) => {
	const [sorting, setSorting] = useState<SortingState>([]);
	const { mipId } = useParams();
	const toastAPI = useDSToast();

	const [selectedDeal, setSelectedDeal] = useState<ISelectedDeal | null>(null);

	const getDealDataRequest = useQuery({
		queryKey: [formatUnicorn(API_PATHS.GODMODE_DEALS_MIPID_GET, { mipId: mipId ?? '' })],
		queryFn: ({ signal }) =>
			getDealData(
				formatUnicorn(API_PATHS.GODMODE_DEALS_MIPID_GET, { mipId: mipId ?? '' }),
				signal
			),
	});

	const getDealsListDataRequest = useQuery({
		queryKey: [formatUnicorn(API_PATHS.GODMODE_DEALS_GET, { mipId: mipId ?? '' })],
		queryFn: ({ signal }) =>
			getDealsListData(
				formatUnicorn(API_PATHS.GODMODE_DEALS_GET, { mipId: mipId ?? '' }),
				signal
			),
	});
	const changeDealRequest = useMutation({
		mutationFn: changeDeal,
	});

	const reParentDeal = useCallback(() => {
		if (!mipId || !selectedDeal?.id) {
			return;
		}
		changeDealRequest.mutate(
			{
				mipId,
				body: {
					dealId: selectedDeal.id,
				},
			},
			{
				onError: (error) => {
					logger.error(error);
					toastAPI.error({
						title: 'Error',
						message: "Deal could't be re-parent!",
					});
				},
				onSuccess: () => {
					refreshDealData();
					refetchMipData();
					setSelectedDeal(null);
					toastAPI.success({
						title: 'Success',
						message: 'Successfully completed!',
					});
				},
			}
		);
	}, [selectedDeal?.id]);

	useEffect(() => {
		if (!getDealDataRequest.isError && !getDealsListDataRequest.isError) {
			return;
		}

		logger.error(getDealDataRequest.isError ?? getDealsListDataRequest.isError);
		toastAPI.error({
			title: 'Error',
			message: 'Data cannot be shown!',
		});
	}, [getDealDataRequest.isError, getDealsListDataRequest.isError]);

	const refreshDealData = useCallback(() => {
		void getDealDataRequest.refetch();
	}, []);

	const manageDealOfMipColumns: ColumnDef<SummaryDealTct>[] = useMemo(() => {
		return [
			{
				id: 'dealId',
				accessorFn: (row) => row.id,
				size: 40,
				header: () => <span>Deal ID</span>,
				cell: (info) => {
					return <span className="support-app-compact-text">{info.row.original.id}</span>;
				},
			},
			{
				id: 'name',
				accessorFn: (row) => row.name,
				header: () => <span>Name</span>,
				size: 30,
				cell: (info) => {
					return (
						<span className="support-app-compact-text">{info.row.original.name}</span>
					);
				},
			},
			{
				id: 'targetName',
				accessorFn: (row) => row.targetName,
				size: 30,
				header: () => <span>Target name</span>,
				cell: (info) => {
					return (
						<span className="support-app-compact-text">
							{info.row.original.targetName}
						</span>
					);
				},
			},
			{
				id: 'action',
				header: () => <span>Action</span>,
				enableSorting: false,
				cell: (info) => {
					return (
						<button
							className="button is-link"
							onClick={() => {
								setSelectedDeal({
									id: info.row.original.id,
									name: info.row.original.name,
								});
							}}
						>
							Re-parent to this deal
						</button>
					);
				},
			},
		];
	}, [getDealsListDataRequest?.isLoading, getDealDataRequest?.data?.payload?.id]);
	const dealData = useMemo(() => {
		return getDealsListDataRequest?.data?.payload ?? EMPTY_DATA;
	}, [getDealsListDataRequest?.isLoading]);
	const tanstackTable = useReactTable({
		data: dealData,
		columns: manageDealOfMipColumns,
		onSortingChange: setSorting,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		state: {
			sorting,
		},
	});
	const tableContainerRef = useRef<HTMLTableElement>(null);
	const { rows } = tanstackTable.getRowModel();
	const rowVirtualizer = useVirtualizer({
		getScrollElement: () => tableContainerRef.current,
		count: rows.length,
		estimateSize: () => 50,
	});

	return {
		isDataLoading: getDealDataRequest.isLoading,
		deal: getDealDataRequest?.data?.payload,
		tableContainerRef,
		rows,
		rowVirtualizer,
		tanstackTable,
		selectedDeal,
		setSelectedDeal,
		reParentDeal,
	};
};
